import { z } from "zod";
import { boundingRect, elementBox, unionRects } from "../scene/geometry";
import {
  type ClipboardData,
  clone,
  copyElements,
  createId,
  createShapeElement,
} from "../scene/ops";
import { scaleTextMarks } from "../scene/richtext";
import {
  clipColorSchema,
  elementMediaId,
  elementSchema,
  type SceneDocument,
  type SceneElement,
} from "../scene/schema";

/**
 * Items an organization saves for reuse (docs/04-dokumentformat.md
 * "Bausteine"): groups of elements ("Meine Bausteine") and colour looks of
 * clips ("Meine Looks"). Stored in `library_item.data`.
 */

export const LIBRARY_ITEM_KINDS = ["element", "look"] as const;
export type LibraryItemKind = (typeof LIBRARY_ITEM_KINDS)[number];

const id = z.string().min(1).max(64);

/** A saved selection: page-space elements, times relative to the block's start. */
export const savedElementsSchema = z
  .object({
    /** Page the elements were saved from (they scale with the target page). */
    format: z.object({ width: z.number().positive(), height: z.number().positive() }),
    /** Video blocks carry times and animations. */
    kind: z.enum(["design", "video"]),
    roots: z.array(id).min(1).max(100),
    elements: z.record(id, elementSchema),
    /** Video: how long the block is visible (ms). */
    duration: z
      .number()
      .min(0)
      .max(24 * 60 * 60 * 1000)
      .optional(),
  })
  .superRefine((data, ctx) => {
    const count = Object.keys(data.elements).length;
    if (count > 300) ctx.addIssue({ code: "custom", message: "Zu viele Elemente (höchstens 300)" });
    for (const root of data.roots) {
      if (!data.elements[root]) {
        ctx.addIssue({ code: "custom", message: `Element "${root}" fehlt` });
      }
    }
    for (const element of Object.values(data.elements)) {
      if (element.type === "captions") {
        ctx.addIssue({
          code: "custom",
          message: "Untertitel lassen sich nicht als Baustein speichern",
        });
      }
      if (element.type === "group") {
        for (const child of element.children) {
          if (!data.elements[child]) {
            ctx.addIssue({ code: "custom", message: `Element "${child}" fehlt` });
          }
        }
      }
    }
  });
export type SavedElements = z.infer<typeof savedElementsSchema>;

export const savedLookSchema = z.object({ values: clipColorSchema.omit({ look: true }) });
export type SavedLook = z.infer<typeof savedLookSchema>;

/** Asset ids used by saved elements (access checks). */
export function savedElementAssetIds(data: SavedElements): string[] {
  const ids = new Set<string>();
  for (const element of Object.values(data.elements)) {
    const assetId = elementMediaId(element);
    if (assetId) ids.add(assetId);
  }
  return [...ids];
}

/** Limits of a saved block (see `savedElementsSchema`). */
export const SAVED_BLOCK_LIMITS = { roots: 100, elements: 300 } as const;

/**
 * Captures the selection for saving: page-space copies, captions left out,
 * times moved so the earliest element starts at 0. Null when nothing is left;
 * throws (with a message for the user) when the selection is too large.
 */
export function captureElements(doc: SceneDocument, ids: readonly string[]): SavedElements | null {
  const usable = ids.filter((elementId) => {
    const element = doc.elements[elementId];
    return element && element.type !== "captions";
  });
  if (usable.length === 0) return null;
  if (usable.length > SAVED_BLOCK_LIMITS.roots) {
    throw new Error(
      `Ein Baustein kann höchstens ${SAVED_BLOCK_LIMITS.roots} Elemente enthalten – gruppiere Teile oder wähle weniger aus.`,
    );
  }
  // Paint order of the page, so the block stacks like the original.
  const order = new Map<string, number>();
  let index = 0;
  const visit = (list: readonly string[]) => {
    for (const child of list) {
      order.set(child, index++);
      const element = doc.elements[child];
      if (element?.type === "group") visit(element.children);
    }
  };
  for (const page of doc.pages) visit(page.children);
  const sorted = [...usable].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
  const data: ClipboardData = copyElements(doc, sorted);
  if (Object.keys(data.elements).length > SAVED_BLOCK_LIMITS.elements) {
    throw new Error(
      `Ein Baustein kann höchstens ${SAVED_BLOCK_LIMITS.elements} Elemente enthalten (mit allen Teilen der Gruppen).`,
    );
  }
  const video = doc.kind === "video";
  const elements = Object.values(data.elements);
  let duration: number | undefined;
  if (video) {
    const times = elements.flatMap((element) => (element.time ? [element.time] : []));
    const start = times.length > 0 ? Math.min(...times.map((time) => time.start)) : 0;
    const end = times.length > 0 ? Math.max(...times.map((time) => time.end)) : 0;
    for (const element of elements) {
      if (element.time) {
        element.time = { start: element.time.start - start, end: element.time.end - start };
      }
    }
    if (end > start) duration = end - start;
  } else {
    for (const element of elements) {
      delete element.time;
      delete element.animation;
    }
  }
  return savedElementsSchema.parse({
    format: { width: doc.format.width, height: doc.format.height },
    kind: video ? "video" : "design",
    roots: data.roots,
    elements: data.elements,
    ...(duration ? { duration } : {}),
  });
}

/**
 * Keeps scaled values inside the document schema (a block from a reel in a
 * big poster must not make the file unsaveable).
 */
export function clampToSchema(element: SceneElement): void {
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  if (element.shadow) {
    element.shadow.x = clamp(element.shadow.x, -500, 500);
    element.shadow.y = clamp(element.shadow.y, -500, 500);
    element.shadow.blur = clamp(element.shadow.blur, 0, 500);
  }
  if (element.type === "text") {
    element.style.fontSize = clamp(element.style.fontSize, 1, 2000);
    for (const mark of element.marks ?? []) {
      if (mark.fontSize !== undefined) mark.fontSize = clamp(mark.fontSize, 1, 2000);
    }
    if (element.style.stroke) element.style.stroke.width = clamp(element.style.stroke.width, 0, 50);
  }
  if ((element.type === "shape" || element.type === "image") && element.stroke) {
    element.stroke.width = clamp(element.stroke.width, 0, 500);
  }
}

/**
 * Media of saved elements that no longer exist become image placeholders in
 * the same place (QR codes drop their logo, device mockups their
 * screenshot), so inserting the block never adds unknown media to a file.
 */
export function withoutMissingMedia(
  data: SavedElements,
  existing: ReadonlySet<string>,
): SavedElements {
  const missing = Object.values(data.elements).filter(
    (element) =>
      (element.type === "image" || element.type === "svg") && !existing.has(element.assetId),
  );
  // QR codes whose logo is gone simply lose the logo.
  const lostLogos = Object.values(data.elements).filter(
    (element) => element.type === "qr" && element.logo && !existing.has(element.logo.assetId),
  );
  // Devices whose screenshot is gone show an empty screen.
  const lostScreens = Object.values(data.elements).filter(
    (element) => element.type === "device" && element.assetId && !existing.has(element.assetId),
  );
  if (missing.length === 0 && lostLogos.length === 0 && lostScreens.length === 0) return data;
  const elements = { ...data.elements };
  for (const element of lostScreens) {
    if (element.type !== "device") continue;
    const { assetId: _assetId, ...rest } = element;
    elements[element.id] = rest;
  }
  for (const element of lostLogos) {
    if (element.type !== "qr") continue;
    const { logo: _logo, ...rest } = element;
    elements[element.id] = rest;
  }
  for (const element of missing) {
    elements[element.id] = createShapeElement("rect", {
      id: element.id,
      ...(element.name ? { name: element.name } : {}),
      x: element.x,
      y: element.y,
      width: element.width,
      height: element.height,
      rotation: element.rotation,
      opacity: element.opacity,
      fill: "#e4e4e7",
      radius: element.type === "image" ? element.radius : 0,
      placeholder: "image",
      // The frame keeps the form of the image (masks are relative to the box).
      ...(element.type === "image" && element.mask ? { mask: { ...element.mask } } : {}),
      ...(element.time ? { time: { ...element.time } } : {}),
      ...(element.animation ? { animation: clone(element.animation) } : {}),
    });
  }
  return { ...data, elements };
}

/** Scales an element's box, text and strokes by `k` (children are relative to their group). */
function scaleElement(element: SceneElement, k: number, all: Record<string, SceneElement>) {
  element.x *= k;
  element.y *= k;
  element.width = Math.max(1, element.width * k);
  element.height = Math.max(1, element.height * k);
  if (element.shadow) {
    element.shadow.x *= k;
    element.shadow.y *= k;
    element.shadow.blur *= k;
  }
  switch (element.type) {
    case "text":
      element.style.fontSize = Math.max(1, element.style.fontSize * k);
      if (element.style.stroke) element.style.stroke.width *= k;
      scaleTextMarks(element, k);
      break;
    case "shape":
    case "image":
      element.radius *= k;
      if (element.stroke) element.stroke.width *= k;
      break;
    case "group":
      for (const child of element.children) {
        const target = all[child];
        if (target) scaleElement(target, k, all);
      }
      break;
    default:
      break;
  }
  clampToSchema(element);
}

export interface PlacedElements {
  /** Top-level elements in paint order (insert them on the page). */
  roots: SceneElement[];
  /** Every element including group children, with fresh ids. */
  elements: SceneElement[];
}

/**
 * Saved elements ready to insert into a page of `frame`: fresh ids, scaled
 * with the page's shorter side and at the same relative position. Video
 * blocks start at `at`; in designs times and animations are dropped.
 */
export function placeSavedElements(
  data: SavedElements,
  target: { width: number; height: number; kind: "design" | "video" },
  at = 0,
): PlacedElements {
  const copy = clone(data.elements);
  const idMap = new Map<string, string>();
  for (const oldId of Object.keys(copy)) idMap.set(oldId, createId(oldId.split("_")[0] || "el"));
  const k = Math.min(target.width, target.height) / Math.min(data.format.width, data.format.height);
  // Uniform scale keeps the proportions; the centre keeps its relative position.
  const rootBounds = unionRects(
    data.roots.flatMap((root) => (copy[root] ? [boundingRect(elementBox(copy[root]!))] : [])),
  ) ?? { x: 0, y: 0, width: 0, height: 0 };
  const center = {
    x: ((rootBounds.x + rootBounds.width / 2) / data.format.width) * target.width,
    y: ((rootBounds.y + rootBounds.height / 2) / data.format.height) * target.height,
  };
  const out: Record<string, SceneElement> = {};
  for (const [oldId, element] of Object.entries(copy)) {
    element.id = idMap.get(oldId)!;
    if (element.type === "group") {
      element.children = element.children.flatMap((child) => {
        const mapped = idMap.get(child);
        return mapped ? [mapped] : [];
      });
    }
    if (target.kind === "video" && data.kind === "video") {
      if (element.time) {
        element.time = {
          start: Math.round(element.time.start + at),
          end: Math.round(element.time.end + at),
        };
      }
    } else {
      delete element.time;
      delete element.animation;
    }
    out[element.id] = element;
  }
  const roots = data.roots.flatMap((root) => {
    const mapped = idMap.get(root);
    return mapped && out[mapped] ? [out[mapped]!] : [];
  });
  for (const root of roots) scaleElement(root, k, out);
  const scaled = unionRects(roots.map((root) => boundingRect(elementBox(root))));
  if (scaled) {
    const dx = center.x - (scaled.x + scaled.width / 2);
    const dy = center.y - (scaled.y + scaled.height / 2);
    for (const root of roots) {
      root.x += dx;
      root.y += dy;
    }
  }
  // A video block from a design (or older blocks) is visible for a while from `at`.
  if (target.kind === "video" && data.kind !== "video") {
    for (const root of roots) root.time = { start: Math.round(at), end: Math.round(at + 4000) };
  }
  return { roots, elements: Object.values(out) };
}
